import type { AdminInterviewDetail } from "@/api/generated/types";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/lib/interview-ui";

type InterviewRecord = NonNullable<AdminInterviewDetail["interview"]>;

export type EvaluationRunInterview = Pick<
  InterviewRecord,
  | "status"
  | "evaluationStartedAt"
  | "evaluationFinishedAt"
  | "evaluationAttemptCount"
  | "evaluationError"
>;

export type EvaluationRunPhase = "queued" | "running" | "succeeded" | "failed" | "none";

export function deriveEvaluationRunPhase(
  interview: Pick<EvaluationRunInterview, "status" | "evaluationStartedAt">
): EvaluationRunPhase {
  switch (interview.status) {
    case "COMPLETED":
      return "succeeded";
    case "FAILED":
      return "failed";
    case "EVALUATING":
      return interview.evaluationStartedAt ? "running" : "queued";
    default:
      return "none";
  }
}

const phaseLabels: Record<EvaluationRunPhase, string> = {
  queued: "評価待機中",
  running: "評価実行中",
  succeeded: "評価完了",
  failed: "評価失敗",
  none: "未評価",
};

const phaseStyles: Record<EvaluationRunPhase, string> = {
  queued: "bg-slate-100 text-slate-600 border-slate-200",
  running: "bg-amber-50 text-amber-700 border-amber-200",
  succeeded: "bg-teal-50 text-teal-700 border-teal-200",
  failed: "bg-rose-50 text-rose-600 border-rose-200",
  none: "bg-slate-100 text-slate-500 border-slate-200",
};

const evaluationErrorMessages: Record<string, string> = {
  no_usable_turns:
    "文字起こしが不足しているため評価を生成できませんでした。面談データを確認して再評価してください。",
  interview_not_found: "対象の面談が見つかりませんでした。",
  evaluation_job_unhandled_exception:
    "評価処理で予期しないエラーが発生しました。再評価を試してください。",
  evaluation_stuck_timeout: "評価処理が一定時間内に完了しませんでした。再評価を試してください。",
};

function formatDurationLabel(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) {
    return "—";
  }

  const elapsedMs = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) {
    return "—";
  }

  return `${Math.round(elapsedMs / 1000)} 秒`;
}

export function EvaluationRunStatus({ interview }: { interview: EvaluationRunInterview }) {
  const phase = deriveEvaluationRunPhase(interview);
  const durationLabel = formatDurationLabel(
    interview.evaluationStartedAt,
    interview.evaluationFinishedAt
  );
  const errorMessage = interview.evaluationError
    ? (evaluationErrorMessages[interview.evaluationError] ?? interview.evaluationError)
    : null;

  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="evaluation-run-status">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-wide text-slate-500">評価実行ステータス</p>
        <Badge variant="outline" className={phaseStyles[phase]}>
          {phaseLabels[phase]}
        </Badge>
      </div>
      <dl className="mt-3 grid gap-x-4 gap-y-2 text-sm text-slate-700 sm:grid-cols-2">
        <div>
          <dt className="text-xs text-slate-500">開始</dt>
          <dd>{formatDateTime(interview.evaluationStartedAt)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">終了</dt>
          <dd>{formatDateTime(interview.evaluationFinishedAt)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">所要時間</dt>
          <dd>{durationLabel}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">実行回数</dt>
          <dd>{interview.evaluationAttemptCount} 回</dd>
        </div>
      </dl>
      {errorMessage && (
        <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          失敗理由: {errorMessage}
        </p>
      )}
    </div>
  );
}
